import {Link as RouterLink, useLocation} from 'react-router-dom';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import {SxProps, useTheme, Tooltip} from '@mui/material';
import {Company} from '@/types/company';
import HomeIcon from '@mui/icons-material/Home';
import {useTranslation} from 'react-i18next';
import {useTitleStyle} from '@/utils';
import ResponsiveTypography from './responsive-typography';
import {useDispatch} from 'react-redux';
import {purgeAppTag} from '@/redux-store/slices/auth';

function handleClick(event: React.MouseEvent<HTMLDivElement, MouseEvent>) {
  event.preventDefault();
}

interface BreadcrumbProps {
  sx?: SxProps;
  company?: Company;
  firstTranslationKey?: string;
  secondTranslationKey?: string;
  thirdTranslationKey?: string;
  skipBreadcrumb?: number;
  homePath?: string;
}

const Breadcrumb: React.FC<BreadcrumbProps> = ({
  sx,
  company,
  firstTranslationKey,
  secondTranslationKey,
  thirdTranslationKey,
  skipBreadcrumb,
  homePath
}) => {
  const {t} = useTranslation();
  const theme = useTheme();
  const breadcrumbStyle = useTitleStyle('18ch', undefined, 'xl', false);
  const dispatch = useDispatch();
  const location = useLocation();
  const pathnames = location.pathname
    .split('/')
    .filter((value) => value !== '');

  if (location.pathname === '/home' || location.pathname === '/') {
    return null;
  }
  const isAtHome = location.pathname === homePath;

  return (
    <div role="presentation" onClick={handleClick}>
      <Breadcrumbs
        aria-label="breadcrumb"
        sx={{
          display: {xs: 'none', md: 'flex'},
          p: 1,
          color: theme.palette.primary.contrastText,
          ...sx
        }}
      >
        {!isAtHome && (
          <Link
            component={RouterLink}
            to={homePath || '/'}
            onClick={() => {
              dispatch(purgeAppTag());
            }}
            sx={{
              color: theme.palette.primary.contrastText
            }}
          >
            <Tooltip title={t('return_to_homepage')}>
              <HomeIcon
                sx={{
                  fontSize: '16px',
                  marginTop: 0.5
                }}
              />
            </Tooltip>
          </Link>
        )}

        {pathnames
          .map((value, index) => {
            let displayValue = value;

            if (index + 1 === skipBreadcrumb) {
              return null;
            }

            if (company && company._id === value) {
              displayValue = company.title;
            }

            if (firstTranslationKey && index === 0) {
              displayValue = t(firstTranslationKey);
            }

            if (secondTranslationKey && index === 1) {
              displayValue = t(secondTranslationKey);
            }

            if (thirdTranslationKey && index === 2) {
              displayValue = t(thirdTranslationKey);
            }

            const last = index === pathnames.length - 1;
            const to = `/${pathnames.slice(0, index + 1).join('/')}`;
            const text = last ? displayValue : `${displayValue} /`;

            return [
              last ? (
                <ResponsiveTypography key={index} sx={breadcrumbStyle}>
                  {text}
                </ResponsiveTypography>
              ) : (
                <Link
                  key={index}
                  underline="none"
                  color="inherit"
                  component={RouterLink}
                  to={to}
                >
                  <ResponsiveTypography key={index} sx={breadcrumbStyle}>
                    {displayValue}
                  </ResponsiveTypography>
                </Link>
              ),
              !last && ' '
            ];
          })
          .flat()}
      </Breadcrumbs>
    </div>
  );
};

export default Breadcrumb;
